import api from './api';

const documentService = {
  // Generate wireframes with streaming (returns a stream of updates)
  generateWireframeStream: async (projectId, prompt, attachments = [], editOptions = undefined) => {
    try {
      // Log the request
      console.log('Streaming wireframe generation for project:', projectId);
      console.log('Prompt:', prompt.substring(0, 100) + '...');
      console.log('Attachments:', attachments);
      console.log('Edit mode:', editOptions ? 'Yes (editing existing wireframe)' : 'No (creating new wireframe)');
      
      // Create a unique key for better tracking
      const uniqueKey = `wireframe-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      // Create the request payload, including edit options if provided
      const payload = {
        prompt,
        attachments,
        uniqueKey,
        // Include edit options if they exist
        ...(editOptions && { 
          isEdit: editOptions.isEdit,
          // Don't include the full HTML in logs
          hasExistingHtml: !!editOptions.existingHtml
        })
      };
      
      console.log('Request payload (without HTML content):', {
        ...payload,
        prompt: payload.prompt.substring(0, 50) + '...'
      });
      
      // Create a response stream handler
      return fetch(`/api/documents/projects/${projectId}/wireframes/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Error generating wireframe stream:', error);
      throw error.response?.data?.message || 'Failed to generate wireframe stream';
    }
  },
  
  // Save the generated wireframe to database
  saveWireframe: async (projectId, html, title = 'AI Generated Wireframe', sectionId, documentType = 'WIREFRAMES', taskId = 'wireframes') => {
    try {
      // Import document mapping utility if needed
      let finalTaskId = taskId || 'wireframes';
      let finalSectionId = sectionId;
      
      // Lazy import to avoid circular dependencies
      if (!finalSectionId) {
        try {
          const { getSectionIdForTask } = require('../utils/documentMapping');
          finalSectionId = getSectionIdForTask(finalTaskId);
        } catch (err) {
          console.warn('Document mapping utilities not available:', err);
        }
      }
      
      // Get the Design section and Wireframes task IDs
      const response = await api.post(`/api/documents/projects/${projectId}/wireframes/save`, {
        html, 
        title,
        sectionId: finalSectionId,
        // Force the document to be assigned to the Design section and wireframes task
        documentType,
        taskId: finalTaskId
      });
      
      // Check if the response includes a contentUrl (for the new file-based approach)
      const documentData = response.data.data;
      
      // Log the document data for debugging
      console.log('Document saved with data:', {
        id: documentData._id,
        type: documentData.documentType,
        sectionId: finalSectionId,
        taskId: finalTaskId,
        hasContentUrl: !!documentData.contentUrl,
        metadata: documentData.metadata
      });
      
      return documentData;
    } catch (error) {
      console.error('Error saving wireframe:', error);
      throw error.response?.data?.message || 'Failed to save wireframe';
    }
  },
  
  // Get a document by ID
  getDocumentById: async (documentId) => {
    try {
      const response = await api.get(`/documents/detail/${documentId}`);
      
      // Add defensive programming for response structure
      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }
      
      // Handle both formats: {data: {...}} and direct object
      const documentData = response.data.data || response.data;
      
      if (!documentData) {
        throw new Error('No document data received from server');
      }
      
      // Check if the document has a contentUrl (for HTML documents)
      if (documentData.contentUrl) {
        console.log(`Document ${documentId} has contentUrl: ${documentData.contentUrl}`);
      }
      
      return documentData;
    } catch (error) {
      console.error('Error fetching document:', documentId, error);
      
      // Provide more detailed error information
      if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
        throw error.response?.data?.detail || error.response?.data?.message || 'Failed to fetch document';
      } else if (error.request) {
        console.error('Network error:', error.request);
        throw 'Network error - could not connect to server';
      } else {
        console.error('Error:', error.message);
        throw error.message || 'Failed to fetch document';
      }
    }
  },

  // Get all documents for a project
  getProjectDocuments: async (projectId) => {
    try {
      const response = await api.get(`/documents/projects/${projectId}`);
      
      // Log HTML documents that have contentUrl
      const documents = response.data.data;
      if (Array.isArray(documents)) {
        const htmlDocs = documents.filter(doc => 
          ['WIREFRAMES', 'DESIGNS', 'DESIGN_SYSTEM'].includes(doc.documentType) && 
          doc.contentUrl
        );
        
        if (htmlDocs.length > 0) {
          console.log(`Found ${htmlDocs.length} HTML documents with contentUrl for project ${projectId}`);
        }
      }
      
      return response.data.data;
    } catch (error) {
      console.error('Error fetching project documents:', projectId, error);
      throw error.response?.data?.message || 'Failed to fetch project documents';
    }
  },

  // Generate APD document from questionnaire responses
  generateAPD: async (projectId) => {
    try {
      const response = await api.post(`/questionnaire/projects/${projectId}/generate-apd`);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate APD document';
    }
  },

  // Update document content - uses the PUT /documents/:documentId endpoint
  updateDocument: async (documentId, content) => {
    try {
      console.log(`Updating document ${documentId} with content length: ${content.length}`);

      // Create a properly structured request that matches what the backend expects
      const requestData = { content };

      // The endpoint calls documentService.createNewVersion in the backend
      const response = await api.put(`/documents/detail/${documentId}`, requestData);

      if (!response || !response.data) {
        throw new Error('Received invalid response from server');
      }

      console.log('Document update response:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Error in updateDocument service:', error);

      // Create a helpful error message for debugging
      let errorMessage = 'Failed to update document: ';

      if (error.response) {
        console.error('Server response error:', error.response.data);
        errorMessage += error.response.data.message || error.response.statusText || 'Server error';
      } else if (error.request) {
        console.error('No response received:', error.request);
        errorMessage += 'No response received from server';
      } else {
        console.error('Error message:', error.message);
        errorMessage += error.message || 'Unknown error';
      }

      // Add a direct download option in the error message
      errorMessage += ' - Please use the Download button to save your work locally';

      throw new Error(errorMessage);
    }
  },

  // Export document as HTML
  exportAsHTML: async (documentId) => {
    try {
      const response = await api.get(`/documents/detail/${documentId}/export/html`, {
        responseType: 'text'
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to export document as HTML';
    }
  },

  // Extract PRD content from project requirement
  extractPRD: async (projectId, content) => {
    try {
      const response = await api.post(`/documents/projects/${projectId}/extract-prd`, { content });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to extract PRD';
    }
  },

  // Generate field validations
  generateFieldValidations: async (documentId, instructions) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `field-validations-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/validations`, { 
        instructions,
        uniqueKey 
      });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate field validations';
    }
  },

  // Generate technology stack guidelines
  generateTechStack: async (documentId) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `tech-stack-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/tech-stack`, { uniqueKey });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate technology stack';
    }
  },

  // Generate development guidelines
  generateDevGuidelines: async (documentId, techStack) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `dev-guidelines-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/dev-guidelines`, { 
        techStack,
        uniqueKey 
      });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate development guidelines';
    }
  },

  // Generate design guidelines
  generateDesignGuidelines: async (documentId, designInfo) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `design-guidelines-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/design-guidelines`, { 
        designInfo,
        uniqueKey 
      });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate design guidelines';
    }
  },

  // Generate security guidelines
  generateSecurityGuidelines: async (documentId) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `security-guidelines-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/security-guidelines`, { uniqueKey });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate security guidelines';
    }
  },

  // Generate API guidelines
  generateAPIGuidelines: async (documentId) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `api-guidelines-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/api-guidelines`, { uniqueKey });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate API guidelines';
    }
  },

  // Generate features
  generateFeatures: async (documentId) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `features-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/features`, { uniqueKey });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate features';
    }
  },

  // Generate user stories
  generateUserStories: async (documentId, features) => {
    try {
      // Generate a unique key for this request
      const uniqueKey = `user-stories-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      
      const response = await api.post(`/documents/detail/${documentId}/user-stories`, { 
        features,
        uniqueKey 
      });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate user stories';
    }
  },

  // Generate final PRD
  generateFinalPRD: async (projectId, documents) => {
    try {
      const response = await api.post(`/documents/projects/${projectId}/final-prd`, { documents });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate final PRD';
    }
  },

  // Connect to Claude Code
  connectToClaudeCode: async (projectId, documents) => {
    try {
      const response = await api.post(`/documents/projects/${projectId}/connect-claude-code`, { documents });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to connect to Claude Code';
    }
  },

  // Setup Git repository
  setupGitRepository: async (projectId, gitConfig) => {
    try {
      const response = await api.post(`/documents/projects/${projectId}/setup-git`, { gitConfig });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to setup git repository';
    }
  },

  // Deploy application
  deployApplication: async (projectId, deployConfig) => {
    try {
      const response = await api.post(`/documents/projects/${projectId}/deploy`, { deployConfig });
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to deploy application';
    }
  },

  // Track PRD progress
  trackPRDProgress: async (projectId) => {
    try {
      console.log('Calling progress API for project:', projectId);
      const response = await api.get(`/documents/projects/${projectId}/progress`);
      console.log('Progress API response:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Error in trackPRDProgress service:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        throw new Error(error.response.data.message || 'Failed to track PRD progress');
      } else if (error.request) {
        console.error('Error request:', error.request);
        throw new Error('No response received from server');
      } else {
        console.error('Error message:', error.message);
        throw new Error(error.message || 'Failed to track PRD progress');
      }
    }
  },
  
  // Create a standalone document from content
  createDocument: async (projectId, documentData) => {
    try {
      const { title, content, type, taskId, sectionId } = documentData;
      
      if (!title || !content || !type) {
        throw new Error('Title, content, and type are required for creating a document');
      }
      
      // Import document mapping utility for validation and enhancement
      let finalTaskId = taskId;
      let finalSectionId = sectionId;
      let validationErrors = [];
      
      // Lazy import to avoid circular dependencies
      try {
        const { validateDocumentData } = require('../utils/documentMapping');
        const validation = validateDocumentData(documentData);
        
        if (!validation.isValid) {
          console.warn('Document validation warnings:', validation.errors);
          validationErrors = validation.errors;
        }
        
        if (validation.enhancedData) {
          finalTaskId = validation.enhancedData.taskId;
          finalSectionId = validation.enhancedData.sectionId;
        }
      } catch (err) {
        console.warn('Document validation not available:', err);
      }
      
      // If we still don't have both taskId and sectionId, log a warning
      if (!finalTaskId || !finalSectionId) {
        console.warn('Creating document without proper section mapping:', {
          title,
          taskId: finalTaskId || 'MISSING',
          sectionId: finalSectionId || 'MISSING',
          validationErrors
        });
      }
      
      console.log(`Creating document for project ${projectId}:`, { 
        title: title.substring(0, 30) + (title.length > 30 ? '...' : ''),
        type,
        taskId: finalTaskId,
        sectionId: finalSectionId,
        contentLength: content.length
      });
      
      const response = await api.post(`/documents/projects/${projectId}`, {
        title,
        content,
        document_type: type,
        task_id: finalTaskId,
        section_id: finalSectionId
      });
      
      return response.data.data;
    } catch (error) {
      console.error('Error creating document:', error);
      
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to create document');
      } else if (error.request) {
        throw new Error('No response received from server when creating document');
      } else {
        throw new Error(error.message || 'Failed to create document');
      }
    }
  },
  
  // Save markdown file to project
  saveMdFile: async (projectId, filename, content, taskId, sectionId) => {
    try {
      // Import document mapping utility if needed
      let finalTaskId = taskId;
      let finalSectionId = sectionId;
      
      // If taskId is not provided, try to determine it from the filename
      if (!finalTaskId) {
        // Extract task from filename - common pattern: 'tech-stack.md', 'wireframes.md', etc.
        const filenameBase = filename.split('.')[0];
        const dashPos = filenameBase.indexOf('-');
        const potentialTaskId = dashPos > 0 ? filenameBase : null;
        
        try {
          const { getSectionIdForTask } = require('../utils/documentMapping');
          if (potentialTaskId && getSectionIdForTask(potentialTaskId)) {
            finalTaskId = potentialTaskId;
            if (!finalSectionId) {
              finalSectionId = getSectionIdForTask(finalTaskId);
            }
          }
        } catch (err) {
          console.warn('Document mapping utilities not available:', err);
        }
      }
      
      const response = await api.post(`/documents/projects/${projectId}/markdown`, {
        filename,
        content,
        taskId: finalTaskId,
        sectionId: finalSectionId
      });
      
      return response.data.data;
    } catch (error) {
      console.error(`Error saving markdown file ${filename}:`, error);
      throw new Error(error.response?.data?.message || 'Failed to save markdown file');
    }
  }
};

export default documentService;